import {
  Box,
  FormControl,
  FormLabel,
  Image,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import {
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
} from '../../../../components/custom';
import CustomDropdown from '../../../../components/custom/CustomDropdown/CustomDropdown';
import TransactionReceipt from '../../../../components/custom/TransactionReceipt';
import { phoneNumberValidation } from '../../../../constants/validationConstants';
import {
  currencyUnformat,
  parsePhoneNumber,
  sortBundles,
} from '../../../../helpers/utilityFunctions';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getBillTransactionsAsync } from '../../../../store/slices/billsPayment/billTransactionsSlice';
import { IGetBillers } from '../../../../store/slices/billsPayment/getBillersCategorySlice';
import {
  payBillAsync,
  resetPayBill,
} from '../../../../store/slices/billsPayment/payBillSlice';
import CreateTransactionPin from '../../../TransactionPin/CreateTransactionPin';
import { TransactionPinForm } from '../TransactionPinForm';
import DataPurchaseSummary from './DataPurchaseSummary';

interface IBuyDataFormProps {
  networks: IGetBillers[];
  selectedBillCategory: string;
  initialBillerName?: string;
  initialPhoneNumber?: string;
}

export const BuyDataForm = ({
  networks,
  selectedBillCategory,
  initialBillerName,
  initialPhoneNumber,
}: IBuyDataFormProps) => {
  const dispatch = useAppDispatch();

  const [page] = useState(1);
  const [limit] = useState(10);

  const [isFixedPrice, setIsFixedPrice] = useState(false);

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  const [step, setStep] = useState(0);

  const [amountLimitError] = useState('');

  const [insufficientBalanceError, setInsufficientBalanceError] = useState('');

  const { value } = useAppSelector((state) => state.auth);

  const { value: acctBalValue } = useAppSelector(
    (state) => state.accountBalance
  );

  const { value: outletsValue } = useAppSelector((state) => state.getOutlets);

  const availableBalance = acctBalValue?.data?.availableBalance;
  const availableBalanceInNaira = availableBalance ? availableBalance / 100 : 0;

  const getMappedNetworkForDropdown = (networkName: string) => {
    const networkOption = providerOptions.find(
      (option) => option.name === networkName
    );

    return {
      name: networkName,
      icon: networkOption ? <Image src={networkOption.icon} alt="" /> : null,
    };
  };

  useEffect(() => {
    dispatch(resetPayBill());
  }, [dispatch]);

  const providerOptions = useMemo(() => {
    return networks
      ?.filter((network) => network.name?.toLowerCase().includes('data'))
      .map((network) => ({
        id: network.id || '',
        name: network.name || '',
        icon: network.imageUrl || '',
      }));
  }, [networks]);

  const initialValues = {
    network: initialBillerName ?? '',
    bundle: '',
    phoneNumber: initialPhoneNumber || '',
    amount: '',
  };

  const validationSchema = Yup.object().shape({
    network: Yup.string().required('Network is required'),
    bundle: Yup.string().required('Bundle is required'),
    phoneNumber: phoneNumberValidation,
    amount: Yup.string().required('Amount is required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const {
    errors,
    touched,
    values,
    getFieldProps,
    isValid,
    setFieldValue,
    handleSubmit,
  } = formik;

  const billerId = networks?.find((n) => n.name === values.network)?.id;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || (/^\+?\d*$/.test(value) && value.length <= 14)) {
      setFieldValue('phoneNumber', value);
    }
  };

  const customerName = `${value?.data?.user?.firstName} ${value?.data?.user?.lastName}`;
  const customerAccountNo = acctBalValue?.data?.accountNumber;
  const accountNameFromStorage = localStorage.getItem('accountName');

  const outletId =
    outletsValue?.data?.find(
      (outlet) =>
        outlet?.account?.accountNumber === customerAccountNo &&
        outlet?.account?.accountName === accountNameFromStorage
    )?.id ?? '';

  const handleFinalSubmit = async (pinValues: { pin: string }) => {
    const selectedBundle = bundles.find((b) => b.name === values.bundle);
    const response = await dispatch(
      payBillAsync({
        outletId,
        amount: Number(values.amount),
        customerId: parsePhoneNumber(values.phoneNumber) || '',
        customerAccountNo: customerAccountNo || 0,
        customerName: customerName,
        billItemId: selectedBundle?.id || '',
        pin: pinValues.pin || '',
        billCategory: selectedBillCategory || '',
        billProvider: values.network,
      })
    );

    if (response?.meta?.requestStatus === 'fulfilled') {
      dispatch(
        getBillTransactionsAsync({
          accountNo: customerAccountNo,
          category: selectedBillCategory.toUpperCase(),
          page,
          limit,
        })
      );
    }
  };

  const { status: paymentStatus, value: paymentValue } = useAppSelector(
    (state) => state.billsPayment
  );

  const bundles = useMemo(
    () =>
      networks?.find((n) => n.name === values.network)
        ?.currentProviderBillerItems || [],
    [networks, values.network]
  );

  const bundleNames = bundles.map((bundle) => bundle.name);

  const sortedBundleNames = sortBundles(bundleNames);

  const billDetails = {
    customerName: customerName,
    customerId: parsePhoneNumber(values.phoneNumber) ?? '',
    billCategory: 'DATA',
    billItemId: bundles?.find((b) => b.name === values.bundle)?.id ?? '',
    billerName: values.network,
    billerId: billerId ?? '',
  };

  useEffect(() => {
    if (values.bundle) {
      setIsFixedPrice(false);
      const selectedBundle = bundles.find((b) => b.name === values.bundle);
      if (selectedBundle?.isFixedPrice) {
        setFieldValue('amount', selectedBundle?.amount?.toString());
        setIsFixedPrice(true);
      }
      setFieldValue('bundleId', selectedBundle?.id || '');
    }
  }, [bundles, setFieldValue, values.bundle]);

  const handleGoForward = () => {
    if (bundles.length > 0 && isValid) {
      const selectedBundle = bundles.find((b) => b.name === values.bundle);
      const bundleAmount = Number(selectedBundle?.amount || 0);

      if (bundleAmount <= availableBalanceInNaira) {
        setStep((prevStep) => prevStep + 1);
      } else {
        setInsufficientBalanceError(
          'Insufficient balance to purchase this bundle.'
        );
      }
    }
  };

  const handleGoBack = () => {
    setStep((currentStep) => currentStep - 1);
  };

  const renderStep = () => {
    const steps = [
      <Box display="flex" flexDirection="column" py="18px">
        <CustomDrawerHeader startsFromBeginning>Data</CustomDrawerHeader>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={8} flex="1">
              <Box mb={6}>
                <Text fontWeight="500" color="#000" mb={1}>
                  Recipient Information
                </Text>
                <Text color="gray.300" fontSize=".875rem">
                  Streamline transactions with seamless information management
                </Text>
              </Box>
              <FormControl
                id="network"
                isInvalid={Boolean(touched.network && errors.network)}
              >
                <FormLabel>Network</FormLabel>
                <CustomDropdown
                  options={providerOptions || []}
                  placeholder="Select an option"
                  getMappedNetwork={getMappedNetworkForDropdown}
                  setFieldValue={setFieldValue}
                  value={values.network}
                  fieldValue="network"
                />
                <CustomFormErrorMessage>
                  {touched.network && errors.network}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="bundle"
                isInvalid={Boolean(touched.bundle && errors.bundle)}
              >
                <FormLabel>Bundle</FormLabel>
                <Select
                  disabled={!sortedBundleNames.length}
                  placeholder="Select an option"
                  isInvalid={Boolean(touched.bundle && errors.bundle)}
                  {...getFieldProps('bundle')}
                  onChange={(e) => {
                    const selectedBundleName = e.target.value;
                    const selectedBundle = bundles.find(
                      (bundle) => bundle.name === selectedBundleName
                    );

                    setFieldValue('bundle', selectedBundleName);
                    setFieldValue('bundleId', selectedBundle?.id || '');
                  }}
                  textTransform="capitalize"
                >
                  {sortedBundleNames?.map((selectedBundleName, index) => (
                    <option key={index} value={selectedBundleName}>
                      {selectedBundleName}
                    </option>
                  ))}
                </Select>

                <CustomFormErrorMessage>
                  {touched.bundle && errors.bundle}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="phoneNumber"
                isInvalid={Boolean(touched.phoneNumber && errors.phoneNumber)}
              >
                <FormLabel>Phone Number</FormLabel>
                <Input
                  {...getFieldProps('phoneNumber')}
                  placeholder="Enter recipient number"
                  type="tel"
                  onChange={handlePhoneNumberChange}
                />
                <CustomFormErrorMessage>
                  {touched.phoneNumber && errors.phoneNumber}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="amount"
                isInvalid={Boolean(
                  touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)
                )}
              >
                <FormLabel>Amount</FormLabel>
                <Input
                  disabled={isFixedPrice || !values.bundle}
                  {...getFieldProps('amount')}
                  value={
                    isFixedPrice
                      ? Number(values.amount).toLocaleString()
                      : values.amount
                  }
                  as="textarea"
                  placeholder="Enter amount"
                  isInvalid={Boolean(
                    touched.amount &&
                      (insufficientBalanceError ||
                        amountLimitError ||
                        errors.amount)
                  )}
                  sx={{
                    pt: 4,
                  }}
                />
                <CustomFormErrorMessage>
                  {touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)}
                </CustomFormErrorMessage>
              </FormControl>
            </Stack>
            <CustomDrawerFooter>
              <PrimaryButton
                onClick={handleGoForward}
                type="submit"
                w="full"
                isDisabled={Boolean(insufficientBalanceError)}
              >
                Continue
              </PrimaryButton>
            </CustomDrawerFooter>
          </Form>
        </FormikProvider>
      </Box>,
      <>
        <CustomDrawerBackButton onClick={handleGoBack} />
        <DataPurchaseSummary
          summary={{
            network: values.network,
            bundle: values.bundle,
            phoneNumber: values.phoneNumber,
            amount: Number(values.amount).toLocaleString(),
            date: new Date(),
          }}
          handleSubmit={handleGoForward}
        />
      </>,
      <>
        <TransactionPinForm
          isSuccess={paymentStatus === 'success'}
          isLoading={paymentStatus === 'loading'}
          isError={paymentStatus === 'failed'}
          onSubmit={handleFinalSubmit}
          goToReceipt={handleGoForward}
          step={step}
          setStep={(currentStep) => setStep(currentStep)}
          statusBoxTitle={paymentValue?.message}
          statusBoxText=""
          billDetails={billDetails}
          twoFactor={{
            type: 'bill_payment',
            amountKobo: Math.round(currencyUnformat(values.amount) * 100),
          }}
        />
      </>,
      <TransactionReceipt
        date={new Date().toDateString()}
        amount={currencyUnformat(values.amount)}
        transactionType="Bill Payment"
        transactionReference="----"
        senderName={accountNameFromStorage ?? customerName}
        narration="Data"
        provider={values.network}
        device={values.phoneNumber}
        transactionFee="₦0.00"
        senderAccountNumber={customerAccountNo}
        showAmountInWords={false}
      />,
    ];

    return <>{steps[step]}</>;
  };

  return (
    <div>
      {!userHasPin ? (
        <CreateTransactionPin setUserHasPin={setUserHasPin} />
      ) : (
        renderStep()
      )}
    </div>
  );
};
